import {Component, Inject, OnDestroy, Renderer2} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {OrderLinesTarget} from '@domain/order-lines-target.enum';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {Permission} from '@domain/profile/permission.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {CarpetWeaveQuality} from '@domain/textile-data/machine-quality/carpet-weave-quality';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {ORDERBOOK, Orderbook} from '@infrastructure/http/orderbook/orderbook';
import {ListDrawingOrderLine} from '@presentation/components/drawing-list/list-drawing-order-line';
import {OverviewListOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-order-line';
import {
  AgGridUtils,
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  DialogBuilderFactoryService,
  DialogType,
  GridModel,
  GridOptionsBuilderFactoryService,
  GridTagComponent,
  NoDataOverlayComponentParams,
  NumberCellEditorComponent,
  NumberCellEditorParams,
  Priority,
  ProgressCellRendererComponent,
  StringUtils,
  TagColor,
  TagSize,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {
  AdvancedFilterModel,
  CellClickedEvent,
  CellEditingStoppedEvent,
  CellRendererSelectorResult,
  CellValueChangedEvent,
  ColDef,
  Column,
  DndSourceCallbackParams,
  EditableCallbackParams,
  FilterModel,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  ICellEditorParams,
  ICellRendererParams,
  IRowNode,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITooltipParams,
  RowDoubleClickedEvent,
  RowNode,
  RowSelectedEvent,
  SortModelItem,
  ValueGetterParams,
  ValueSetterParams
} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {map, Observable, takeUntil} from 'rxjs';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../../../plan/production-schedule-plan';
import {OrderLinesHelper} from '../../order-lines-helper';
import {OrderLineStatusCellRendererParams} from '../../order-lines-view/order-line-status-cell-renderer/order-line-status-cell-renderer-params.interface';
import {OrderLineStatusCellRendererComponent} from '../../order-lines-view/order-line-status-cell-renderer/order-line-status-cell-renderer.component';
import {ProductionScheduleItemInPathGroupStatusCellRendererComponent} from '../../order-lines-view/production-schedule-item-in-path-group-status-cell-renderer/production-schedule-item-in-path-group-status-cell-renderer.component';
import {ProductionScheduleItemInPathGroupWithIdentification} from '../../order-lines-view/production-schedule-item-in-path-group-with-identification';
import {ProductionScheduleOrderLineWithQuantity} from '../../order-lines-view/production-schedule-order-line-with-quantity';
import {GroupedOrderLinesSelectionService} from '../grouped-orderlines-selection.service';
import {OrderLinesGroupedDetailCellRendererData} from './order-lines-grouped-detail-cell-renderer-data.interface';

type RowData = ProductionScheduleOrderLineWithQuantity | ProductionScheduleItemInPathGroupWithIdentification;

@Component({
  templateUrl: './order-lines-grouped-detail-cell-renderer.component.html'
})
export class OrderLinesGroupedDetailCellRendererComponent extends BaseComponent implements ICellRendererAngularComp, OnDestroy {
  public detailOptions: GridOptions;
  public loadingForTheFirstTime = true;

  private detailApi: GridApi;
  private detailGridModel: GridModel;

  private initialIds: string[];
  private masterGridApi: GridApi;
  private masterNode: IRowNode;
  private data: OverviewListOrderLine[];
  private parentState: boolean;
  private rollLengthColumnId = 'rollLength';

  private commercialUnit: Unit;
  private displayTechnicalUnit = true;
  private productionSchedule: ProductionSchedule;
  private isPreSelection: boolean;
  private productionOrderCustomSettings: ProductionOrderCustomSettings;
  private selectedPath: ProductionSchedulePath;
  private selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  private productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  private processRowSelection: boolean = true;
  private availableSpaceInBuggyInPicks: number;
  private buggyLengthMaxInPicks: number;
  private fullQualities: CarpetWeaveQuality[];
  private pathWidths: PathWidth[] = [];
  private marginInDentsOfMissingDesigns: number;
  private hasQuantitySuggestionPermission: boolean = false;
  private productionScheduleItemMultiplier: number = 2;

  private readonly SLICE_SIZE = 30;
  private readonly DETAIL_ID_PREFIX = 'detail_';
  private readonly WIDTH_HEADER_NAME = 'GENERAL.DIMENSIONS.WIDTH';

  public constructor(
    @Inject(AUTHENTICATION) private authentication: Authentication,
    @Inject(ORDERBOOK) private readonly orderbook: Orderbook,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly orderLinesHelper: OrderLinesHelper,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly groupedOrderLinesSelectionService: GroupedOrderLinesSelectionService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly l10nIntlService: L10nIntlService,
    private readonly draggingService: DraggingService,
    private readonly renderer: Renderer2
  ) {
    super();
  }

  public agInit(params: any): void {
    if (!AssertionUtils.isNullOrUndefined(params?.initialIds)) {
      this.initialIds = params.initialIds.map(String);
    }

    this.masterGridApi = params.api;
    this.masterNode = params.node.parent;
    this.parentState = params.parentState;

    this.setDataParams(params);

    this.orderLinesHelper.displayTechnicalUnit = this.displayTechnicalUnit;
    this.orderLinesHelper.commercialUnit = this.commercialUnit;

    this.groupedOrderLinesSelectionService.cellRendererParams.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((updatedParams: any) => {
      this.setDataParams(updatedParams);

      setTimeout(() => {
        this.detailApi?.refreshCells();
      });
    });

    this.setupDetailGrid(params.data.orderlineIds);

    if (this.authentication.getCurrentSubscription()?.hasPermission(Permission.TEXFAB_QUANTITY_SUGGESTION)) {
      this.hasQuantitySuggestionPermission = true;
    }
  }

  public refresh(_: ICellRendererParams<any, any>): boolean {
    return false;
  }

  public ngOnDestroy(): void {
    const detailGridId = 'detail_' + this.masterNode.id;
    this.masterGridApi.removeDetailGridInfo(detailGridId);
  }

  private setDataParams(params: OrderLinesGroupedDetailCellRendererData): void {
    this.displayTechnicalUnit = params.extraData.displayTechnicalUnit;
    this.commercialUnit = params.extraData.commercialUnit;
    this.productionSchedule = params.extraData.productionSchedule;
    this.isPreSelection = params.extraData.isPreSelection;
    this.productionOrderCustomSettings = params.extraData.productionOrderCustomSettings;
    this.selectedPath = params.extraData.selectedPath;
    this.selectedRestPathColoredYarnSet = params.extraData.selectedRestPathColoredYarnSet;
    this.productionSchedulePathsOfColoredYarnSet = params.extraData.productionSchedulePathsOfColoredYarnSet;
    this.availableSpaceInBuggyInPicks = params.extraData.availableSpaceInBuggyInPicks;
    this.buggyLengthMaxInPicks = params.extraData.buggyLengthMaxInPicks;
    this.pathWidths = params.extraData.pathWidths;
    this.fullQualities = params.extraData.fullQualities;
    this.marginInDentsOfMissingDesigns = params.extraData.marginInDentsOfMissingDesigns;
    this.productionScheduleItemMultiplier = params.extraData.productionScheduleItemMultiplier;

    if (!this.isPreSelection) {
      this.detailApi?.forEachNode((rowNode: RowNode<ProductionScheduleOrderLineWithQuantity>) => {
        if (!AssertionUtils.isNullOrUndefined(rowNode.data)) {
          const value = this.orderLinesHelper.getQuantitySuggestionForPath(
            rowNode.data,
            this.productionSchedule,
            this.selectedPath,
            this.availableSpaceInBuggyInPicks,
            this.buggyLengthMaxInPicks,
            this.fullQualities,
            this.hasQuantitySuggestionPermission,
            this.productionScheduleItemMultiplier
          );
          rowNode.setDataValue('quantity', value);
        }
      });
    }
  }

  private addSelectedOrderLinesToPath(colDef: ColDef, data: ProductionScheduleOrderLineWithQuantity): void {
    if (colDef.headerName !== this.translate.instant('ORDERBOOK.QUANTITY') && data.quantity > 0 && colDef.colId !== this.rollLengthColumnId) {
      const selectedOrderLines = [data];

      this.detailApi.getSelectedNodes().forEach((rowNode: RowNode) => rowNode.setSelected(false));

      const listDrawingOrderLines = selectedOrderLines.map((productionScheduleOrderLine: ProductionScheduleOrderLineWithQuantity) =>
        this.orderLinesHelper.createListDrawingOrderLine(
          this.productionSchedule,
          productionScheduleOrderLine,
          this.productionSchedule.machineQuality.weaveStructure.picksPerColorLine,
          this.marginInDentsOfMissingDesigns,
          this.pathWidths,
          false
        )
      );

      listDrawingOrderLines.forEach((listDrawingOrderLine: ListDrawingOrderLine) => {
        let overviewListColoredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent;

        overviewListColoredYarnSetWithStartDent = this.productionSchedule.coloredYarnSets.find(
          (cys: OverviewListColoredYarnSetWithStartDent) => cys.startDent === this.productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.startDent
        );

        if (
          !AssertionUtils.isNullOrUndefined(this.selectedPath) &&
          this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(listDrawingOrderLine, this.selectedPath.technicalWidthInDents)
        ) {
          this.productionSchedulePlan.addDrawingToProductionSchedulePath(listDrawingOrderLine, this.selectedPath.pathNumber, overviewListColoredYarnSetWithStartDent);
        } else if (
          AssertionUtils.isNullOrUndefined(this.selectedPath) &&
          this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(overviewListColoredYarnSetWithStartDent.startDent, listDrawingOrderLine)
        ) {
          this.addSelectedOrderLinesToNewPath(listDrawingOrderLine);
        } else {
          this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
            titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
            messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.PATH_WIDTH_DOES_NOT_EQUAL_DESIGN_WIDTH',
            type: DialogType.INFORMATION
          });
        }
      });
    }
  }

  private addSelectedOrderLinesToNewPath(listDrawingOrderLine: ListDrawingOrderLine): void {
    this.productionSchedulePlan
      .addDrawingToColoredYarnSet(listDrawingOrderLine, this.selectedRestPathColoredYarnSet.startDent, undefined)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((path: ProductionSchedulePath) => {
        if (!AssertionUtils.isNullOrUndefined(path)) {
          this.selectedPath = path;
          this.selectedRestPathColoredYarnSet = undefined;
          this.groupedOrderLinesSelectionService.updateSelectedPath(this.selectedPath);
          this.groupedOrderLinesSelectionService.updateSelectedRestPathColoredYarnSet(this.selectedRestPathColoredYarnSet);
        }
      });
  }

  private onFirstDataRendered(event: FirstDataRenderedEvent): void {
    this.detailApi = event.api;

    if (AssertionUtils.isNullOrUndefined(this.parentState)) {
      this.initialIds?.forEach((id: string) => {
        if (!AssertionUtils.isNullOrUndefined(this.detailApi?.getRowNode(id))) {
          this.detailApi?.getRowNode(id).setSelected(true);
        }
      });
    } else {
      this.detailApi.forEachNode((detailNode: RowNode) => detailNode.setSelected(this.parentState));
    }

    const detailGridId = `${this.DETAIL_ID_PREFIX}${this.masterNode.id}`;
    const gridInfo = {id: detailGridId, api: event.api};

    this.masterGridApi.addDetailGridInfo(detailGridId, gridInfo);

    if (!this.isPreSelection) {
      this.detailApi?.forEachNode((rowNode: RowNode<ProductionScheduleOrderLineWithQuantity>) => {
        if (!AssertionUtils.isNullOrUndefined(rowNode.data)) {
          const value = this.orderLinesHelper.getQuantitySuggestionForPath(
            rowNode.data,
            this.productionSchedule,
            this.selectedPath,
            this.availableSpaceInBuggyInPicks,
            this.buggyLengthMaxInPicks,
            this.fullQualities,
            this.hasQuantitySuggestionPermission,
            this.productionScheduleItemMultiplier
          );
          rowNode.setDataValue('quantity', value);
        }
      });
    }
  }

  private onRowSelected(event: RowSelectedEvent): void {
    if (this.isPreSelection) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectionState = this.groupedOrderLinesSelectionService.getParentSelectionState(
        this.data,
        selectedNodes.map((node: RowNode) => node.data.id)
      );

      const selectedNodeIds = selectedNodes.map((node: RowNode) => node.id);
      const currentlySelectedOrderLines = this.data.filter((orderline: OverviewListOrderLine) => selectedNodeIds.map(Number).includes(orderline.id));

      this.groupedOrderLinesSelectionService.setDetailRowSelected(this.masterNode, selectionState, currentlySelectedOrderLines);
    }
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.orderbook.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }

  private getColumnDefinitionForClickedCell(): ColDef {
    const column = this.detailApi.getAllDisplayedColumns().find((col: Column) => col.getColId() === this.detailApi.getFocusedCell().column.getColId());
    return column ? column.getColDef() : null;
  }

  private getDetailServerSideDataSource(orderlineIds?: number[]): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams): void => {
        const sortModel: SortModelItem[] = params.request.sortModel;
        const startRow: number = params.request.startRow;
        const endRow: number = params.request.endRow;
        const currentFilterModel: FilterModel | AdvancedFilterModel = params.request.filterModel;

        let filterModel =
          !AssertionUtils.isNullOrUndefined(orderlineIds) && !AssertionUtils.isEmpty(orderlineIds)
            ? {
                ...currentFilterModel,
                id: {
                  filterModels: [null, {values: orderlineIds, filterType: 'set'}],
                  filterType: 'multi'
                }
              }
            : currentFilterModel;

        this.detailGridModel = new GridModel(startRow, endRow, sortModel, filterModel);

        this.orderbook
          .getSlice(OrderLinesTarget.PRODUCTION_SCHEDULE, this.detailGridModel, this.productionSchedule?.id?.toString())
          .pipe(
            map((orderLines: OverviewListOrderLine[]) => {
              this.data = orderLines;
              return orderLines.map((overviewListOrderLine: OverviewListOrderLine) =>
                ProductionScheduleOrderLineWithQuantity.fromProductionScheduleOrderLine(ProductionScheduleOrderLine.fromOverviewListOrderLine(overviewListOrderLine))
              );
            }),
            takeUntil(this.unSubscribeOnViewDestroy)
          )
          .subscribe({
            next: (orderLines: ProductionScheduleOrderLineWithQuantity[]) => {
              let rowCount: number;

              if (orderLines.length < this.SLICE_SIZE) {
                rowCount = startRow + orderLines.length;
              }

              if (startRow === 0) {
                if (orderLines.length === 0) {
                  params.api.showNoRowsOverlay();
                } else {
                  params.api.hideOverlay();
                }
              }

              this.loadingForTheFirstTime = false;
              this.detailOptions.loadingCellRendererParams = {
                loadingForTheFirstTime: this.loadingForTheFirstTime
              };

              setTimeout(() => {
                params.success({
                  rowData: orderLines,
                  rowCount
                });
              });
            },
            error: (error: any) => {
              this.orderLinesHelper.onServerSideDataSourceError(params, error);
            }
          });
      }
    };
  }

  private setupDetailGrid(orderLineIds: number[]): void {
    const defaultDetailGridOptions = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getDetailColumnDefinitions(), this.isPreSelection ? GridIdentifier.ORDER_ITEMS_NEW_PRESELECTION : GridIdentifier.ORDER_ITEMS_NEW_BUILDER)
      .withRowSelection(this.isPreSelection, false)
      .withHeaderHeight(32)
      .withFloatingFiltersHeight(32)
      .withServerSideDataSource(this.SLICE_SIZE, this.getDetailServerSideDataSource(orderLineIds))
      .withStopEditingWhenCellsLoseFocus()
      .withCompactView()
      .withPopupParent(document.querySelector('body'))
      .withOnRowSelected((event: RowSelectedEvent) => this.onRowSelected(event))
      .withOnFirstDataRendered((event: FirstDataRenderedEvent) => this.onFirstDataRendered(event))
      .withNoRowsOverlay({
        scale: 0.2,
        titleParam: 'ORDERBOOK.ORDERLINE',
        hideDescription: true,
        isAnyFilterPresent: () => !AssertionUtils.isNullOrUndefined(this.detailApi) && Object.values(this.detailApi?.getFilterModel())?.length > 0
      } as NoDataOverlayComponentParams);

    if (this.isPreSelection) {
      this.detailOptions = defaultDetailGridOptions.build();
    } else {
      this.detailOptions = defaultDetailGridOptions
        .withOnRowSelected((event: RowSelectedEvent) => {
          setTimeout(() => {
            if (this.processRowSelection && this.hasQuantitySuggestionPermission) {
              this.orderLinesHelper.setQuantitySuggestionForPath(event, this.productionSchedule, this.selectedPath, this.availableSpaceInBuggyInPicks, this.buggyLengthMaxInPicks, this.fullQualities);
            }
          }, 500);
        })
        .withOnRowDoubleClicked((event: RowDoubleClickedEvent) => {
          this.processRowSelection = false;
          const colDef = this.getColumnDefinitionForClickedCell();
          this.addSelectedOrderLinesToPath(colDef, event.data);
          setTimeout(() => {
            this.processRowSelection = true;
          }, 1000);
        })
        .withOnCellValueChanged((event: CellValueChangedEvent) => {
          this.addSelectedOrderLinesToPath(event.colDef, event.data);
        })
        .withRowDraggingOutsideGrid(
          (params: {rowNode: RowNode; dragEvent: DragEvent}) =>
            this.orderLinesHelper.onRowDrag(params, this.productionSchedule, this.draggingService, this.renderer, this.pathWidths, this.marginInDentsOfMissingDesigns),
          (params: DndSourceCallbackParams<ProductionScheduleOrderLine | ProductionScheduleItemInPathGroupWithIdentification>) => params.node.level === 0,
          1
        )
        .withOnCellEditingStopped((event: CellEditingStoppedEvent) => {
          if (this.hasQuantitySuggestionPermission) {
            this.orderLinesHelper.recalculateQuantitySuggestionWhenRollLengthIsChanged(
              event,
              this.rollLengthColumnId,
              this.productionSchedule,
              this.selectedPath,
              this.buggyLengthMaxInPicks,
              this.availableSpaceInBuggyInPicks,
              this.fullQualities
            );
          }
        })
        .build();
    }
  }

  private getDetailColumnDefinitions(): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withColIdAndField('id').withNumericMultiFilter(this.getPossibleColumnsValues('id')).withHide().withHiddenInToolPanel().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('add')
        .withHeaderName('PRODUCTION_ORDER.DESIGN_LIBRARY.ORDER_LINES.ADD_TO_PATH')
        .withCellRenderer(ProgressCellRendererComponent, {
          label: 'GENERAL.ACTIONS.ADD',
          isOrderline: true
        })
        .withoutFilter()
        .withSuppressMovable()
        .withMinWidth(112)
        .withMaxWidth(150)
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('quantity')
        .withHeaderName('ORDERBOOK.QUANTITY')
        .withRightAlignment()
        .withSortable(false)
        .withEditable((params: EditableCallbackParams<RowData>) => this.orderLinesHelper.isQuantityEditable(params, this.productionSchedule, this.productionOrderCustomSettings))
        .withoutFilter()
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withCellEditor(
          NumberCellEditorComponent,
          (params: ICellEditorParams<RowData>): NumberCellEditorParams =>
            ({
              min:
                params.node.level === 0 && params.data instanceof ProductionScheduleOrderLineWithQuantity && params.data.article.design.repeated
                  ? 1
                  : this.productionSchedule.getProductionScheduleItemMultiplier(),
              max:
                !this.productionOrderCustomSettings?.allowExceedingRequestedAmountOfOrderLineItems && params.node.level === 0 && params.data instanceof ProductionScheduleOrderLineWithQuantity
                  ? this.orderLinesHelper.getMaxQuantityToPlan(params.data, this.productionSchedule)
                  : undefined,
              autoSelect: true
            }) as NumberCellEditorParams
        )
        .withValueGetter((params: ValueGetterParams<RowData>) => {
          if (params.data instanceof ProductionScheduleOrderLineWithQuantity && params.data?.quantity) {
            return params.data.quantity;
          } else if (params.data instanceof ProductionScheduleOrderLineWithQuantity) {
            return this.orderLinesHelper.getQuantitySuggestionForPath(
              params.data,
              this.productionSchedule,
              this.selectedPath,
              this.availableSpaceInBuggyInPicks,
              this.buggyLengthMaxInPicks,
              this.fullQualities,
              this.hasQuantitySuggestionPermission,
              this.productionScheduleItemMultiplier
            );
          }
        })
        .withValueSetter((params: ValueSetterParams<ProductionScheduleOrderLineWithQuantity>) => this.setQuantity(params))
        .withSuppressMovable()
        .withHide(this.isPreSelection)
        .withWidth(100)
        .withMinWidth(100)
        .withMaxWidth(150)
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId(this.rollLengthColumnId)
        .withField(`rollLengthInMeter`)
        .withHeaderName('PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.ROLL_LENGTH', undefined, false, Unit.METER)
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withSortable(false)
        .withEditable((params: EditableCallbackParams<ProductionScheduleOrderLineWithQuantity>) => this.orderLinesHelper.isRollLengthEditable(params))
        .withCellEditor(NumberCellEditorComponent, (): NumberCellEditorParams => ({isSmall: false, autoSelect: true}) as NumberCellEditorParams)
        .withValueGetter((params: ValueGetterParams<ProductionScheduleOrderLineWithQuantity>) => {
          if (params.data?.rollLengthInMeter && params.data?.article?.commercialHeightInMM / 1000 < params.data.rollLengthInMeter) {
            return params.data.rollLengthInMeter;
          } else if (params.data?.article?.design?.repeated) {
            const rollLengthInMeter = params.data?.article?.commercialHeightInMM / 1000;
            if (!AssertionUtils.isNullOrUndefined(rollLengthInMeter)) {
              params.data.rollLengthInMeter = rollLengthInMeter;
              return params.data.rollLengthInMeter;
            } else {
              return 1;
            }
          } else {
            return null;
          }
        })
        .withMinWidth(130)
        .withoutFilter()
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel(this.isPreSelection)
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('amountLeft')
        .withValueGetter((params: ValueGetterParams<RowData>): number => this.orderLinesHelper.getAmountLeft(params.data, this.productionSchedule))
        .withHeaderName('ORDERBOOK.TO_PLAN_AMOUNT')
        .withSortable(false)
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountLeft'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('totalCount')
        .withValueGetter((params: ValueGetterParams<RowData>): number => this.orderLinesHelper.getTotalCount(params))
        .withHeaderName('ORDERBOOK.REQUESTED_AMOUNT')
        .withSortable(false)
        .withRightAlignment()
        .withNumericMultiFilter(this.getPossibleColumnsValues('totalCount'))
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('salesOrderReference', true)
        .withHeaderName('ORDERBOOK.ORDERBOOK_REFERENCE')
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('salesOrderReference'))
        .withLockVisible()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('salesOrderLineReference', true)
        .withHeaderName('ORDERBOOK.ORDERLINE_REFERENCE')
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('salesOrderLineReference'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('article')
        .withField('article.name', true)
        .withHeaderName('ARTICLES.ARTICLE')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('article'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('designCode')
        .withField('article.design.code', true)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.DESIGN_CODE')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('designCode'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('commercialWidthInCM')
        .withField('article.commercialWidthInMM')
        .withHeaderName(this.WIDTH_HEADER_NAME, 1, false, Unit.CENTIMETER)
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithoutUnit(params.data.article.commercialWidthInMM, Unit.MILLIMETER, Unit.CENTIMETER, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnitConversion(params.value, Unit.MILLIMETER, this.commercialUnit, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('commercialWidthInMM'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('commercialHeightInCM')
        .withField('article.commercialHeightInMM')
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.CENTIMETER)
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithoutUnit(params.data.article.commercialHeightInMM, Unit.MILLIMETER, Unit.CENTIMETER, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnitConversion(params.value, Unit.MILLIMETER, this.commercialUnit, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('commercialHeightInMM'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('technicalWidthInDents')
        .withField('article.technicalWidthInDents')
        .withHeaderName(this.WIDTH_HEADER_NAME, 1, false, Unit.DENTS)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.DENTS, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('technicalWidthInDents'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('technicalHeightInPicks')
        .withField('article.technicalHeightInPicks')
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.PICKS)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PICKS, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('technicalHeightInPicks'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('amountInProductionSchedule')
        .withValueGetter((params: ValueGetterParams<RowData>): number => this.orderLinesHelper.getAmountInProductionSchedule(params.data, this.productionSchedule))
        .withHeaderName('ORDERBOOK.AMOUNT_IN_PRODUCTION_SCHEDULE')
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel(this.isPreSelection)
        .withRightAlignment()
        .withSortable(false)
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountInProductionSchedule'))
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('machineQualityName')
        .withField('article.machineQuality.nameWithVersion')
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withTooltipField('article.machineQuality.name')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('machineQualityName'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('coloredYarnSet')
        .withField('article.coloredYarnSet.name', true)
        .withHeaderName('TEXTILE_DATA.COLORED_YARN_SET.COLORED_YARN_SET')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('coloredYarnSet'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('mainColor')
        .withField('article.mainColor.name', true)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.MAIN_COLOR')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('mainColor'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('borderColor')
        .withField('article.borderColor.name', true)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.BORDER_COLOR')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('borderColor'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('shape')
        .withField('article.design.shape', true)
        .withHeaderName('GENERAL.SHAPE')
        .withComparator(StringUtils.stringComparator)
        .withValueGetter((params: ValueGetterParams) => this.getShape(params.data.article?.design?.shape), true)
        .withTextMultiFilter(this.getPossibleColumnsValues('shape'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('finishingName')
        .withField('article.finishing.nameWithVersion', true)
        .withHeaderName('TEXTILE_DATA.FINISHING.FINISHING')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('finishingName'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('desiredDate')
        .withHeaderName('SALES_ORDERS.DESIRED_DATE')
        .withDate()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withDateMultiFilter(this.getPossibleColumnsValues('desiredDate'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('customerReference', true)
        .withHeaderName('ORDERBOOK.CUSTOMER_REFERENCE')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('customerReference'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('priority')
        .withHeaderName('SALES_ORDERS.PRIORITY.PRIORITY')
        .withCellRenderer(GridTagComponent, {
          translationKey: 'SALES_ORDERS.PRIORITY.',
          size: TagSize.SMALL,
          colorGetter: {
            [Priority.LOW]: TagColor.PRIMARY,
            [Priority.MEDIUM]: TagColor.WARNING,
            [Priority.HIGH]: TagColor.ERROR
          }
        })
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('priority'))
        .withTooltipValueGetter((params: ITooltipParams) => this.translate.instant(`SALES_ORDERS.PRIORITY.${params.value}`).toUpperCase())
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('status')
        .withHeaderName('GENERAL.STATUS')
        .withCellRendererSelector(
          (params: ICellRendererParams): CellRendererSelectorResult =>
            params.node.level === 0
              ? {
                  component: OrderLineStatusCellRendererComponent,
                  params: {
                    productionSchedule: this.productionSchedule
                  } as OrderLineStatusCellRendererParams
                }
              : {
                  component: ProductionScheduleItemInPathGroupStatusCellRendererComponent,
                  params: {
                    productionSchedule: this.productionSchedule
                  }
                }
        )
        .withLockVisible()
        .withTextMultiFilter(this.getPossibleColumnsValues('status'))
        .withTooltipValueGetter((params: ITooltipParams) =>
          params.node.level === 0
            ? this.translate.instant(`ORDERBOOK.ORDER_LINE_STATUS.${params.value}`).toUpperCase()
            : this.translate.instant('ORDERBOOK.IN_PATH', {pathNumber: params?.data?.productionSchedulePath?.pathNumber})
        )
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('amountProduced')
        .withHeaderName('ORDERBOOK.PRODUCED_AMOUNT')
        .withValueGetter((params: ValueGetterParams) => {
          return `${params.data.amountProduced}`;
        })
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountProduced'))
        .withHide()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('amountPlanned')
        .withHeaderName('ORDERBOOK.PLANNED_AMOUNT')
        .withValueGetter((params: ValueGetterParams) => {
          return `${params.data.amountPlanned}`;
        })
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountPlanned'))
        .withHide()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('orderType')
        .withHeaderName('ORDERBOOK.ORDER_TYPE')
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('orderType'))
        .withHide()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('designName')
        .withField('article.design.name', true)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.DESIGN_NAME')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('designName'))
        .withHide()
        .build()
    ];
  }

  private editQuantityCell(event: CellClickedEvent): void {
    if (this.detailApi) {
      this.detailApi?.stopEditing();
    }

    if (event.column.getColDef().editable) {
      this.detailApi.startEditingCell({
        rowIndex: event?.rowIndex,
        colKey: event?.column?.getColId()
      });
    }
  }

  private setQuantity(params: ValueSetterParams<ProductionScheduleOrderLineWithQuantity>): boolean {
    if (this.productionScheduleItemMultiplier === 2 && params.newValue % 2 !== 0) {
      params.newValue--;
    }

    params.node.data.quantity = params.newValue;

    return true;
  }

  private getShape(shape: string): string {
    return FixedShape.getTranslationKeyForShape(shape, this.translate);
  }
}
